/**
 * You — the v2 profile screen.
 *
 * Implements the Profile screen of AppScreen.dc.html ("OnFlow Design System"):
 *
 *   header      → name + email + initial avatar
 *   goal card   → TrackRing over goal progress vs window elapsed, the locked
 *                 goal sentence, and the route into tracker setup
 *   stat tiles  → sessions this week / total sessions / days logged
 *   body rows   → height, weight, age, sex, activity (from the AI profile)
 *   plan rows   → routines, nutrition target, weekly session goal
 *   preferences → units segment + rest-timer chips, both writing straight to
 *                 PATCH /v1/me/profile
 *   account     → sign out
 *
 * Deviation from the template: no "Change goal" bottom sheet — goal editing
 * already has a dedicated multi-step wizard at /onboarding, and duplicating
 * its validation inside a sheet would fork the goal-lock rules.
 */
import type { BillingStatusResponse, Gender, Units, UserProfileAI } from '@onflow/shared';
import { Link } from 'react-router-dom';

import { SkeletonBlock } from '../components/SkeletonBlock.js';
import { FlowButton } from '../components/v2/FlowButton.js';
import { FlowSegment } from '../components/v2/FlowInput.js';
import { FlowStatus, type TrackState } from '../components/v2/FlowStatus.js';
import { Icon } from '../components/v2/Icon.js';
import { TrackRing } from '../components/v2/TrackRing.js';
import { WeightLogWidget } from '../components/WeightLogWidget.js';
import { useLogout, useMe, useUpdateProfile } from '../hooks/useAuth.js';
import { useBillingStatus, useCancelBillingSubscription } from '../hooks/useBilling.js';
import { useOnboardingProfile } from '../hooks/useOnboarding.js';
import { useCurrentReviewQuery } from '../hooks/useReview.js';
import { useRoutines } from '../hooks/useRoutines.js';
import { useFrequency, useVolume } from '../hooks/useStats.js';
import { useNutritionTarget } from '../hooks/useTargets.js';
import { useWeightLogsQuery } from '../hooks/useWeightLogs.js';
import { daysLeft, goalHeadline, goalProgressPct, projectionState } from '../lib/track.js';

import '../styles/profile.css';

const EYEBROW = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  letterSpacing: '0.12em',
  textTransform: 'uppercase' as const,
  color: 'var(--ink-3)',
};

const CARD = {
  background: 'var(--paper-1)',
  border: '1px solid var(--edge)',
  borderRadius: 'var(--r-md)',
  boxShadow: 'var(--elev-1)',
};

const REST_OPTIONS = [60, 90, 120];

export function Profile() {
  const { data, isLoading } = useMe();
  const logout = useLogout();
  const updateProfile = useUpdateProfile();
  const aiProfile = useOnboardingProfile();
  const weights = useWeightLogsQuery({ page: 1, pageSize: 1 });
  const review = useCurrentReviewQuery();
  const target = useNutritionTarget();
  const billing = useBillingStatus();
  const routines = useRoutines();
  const volume = useVolume('12w');
  const frequency = useFrequency('12w');

  if (isLoading) {
    return (
      <section className="fade-in">
        <div className="empty-state card">
          <p>Loading profile…</p>
        </div>
      </section>
    );
  }
  if (!data) return null;

  const now = new Date();
  const p = aiProfile.data ?? null;
  const modes = p?.trackingModes ?? ['nutrition', 'training'];
  const nutritionEnabled = modes.includes('nutrition');
  const trainingEnabled = modes.includes('training');
  const latestWeightKg = weights.data?.items[0]?.weightKg ?? p?.currentWeightKg ?? null;
  const state = review.data?.status ?? projectionState(review.data?.projection);
  const ringState = state === 'neutral' ? 'drifting' : state;
  const progress = goalProgressPct(p?.currentWeightKg, p?.targetWeightKg, latestWeightKg) ?? 0;
  const left = daysLeft(p?.targetDate ?? null, now);
  const elapsed = windowElapsedPct(p?.createdAt ?? null, p?.targetDate ?? null, now);

  const perDay =
    review.data?.review.content.schemaVersion === 'v2'
      ? review.data.review.content.adherence
      : null;
  const totalWorkouts = volume.data?.points.reduce((s, pt) => s + (pt.workouts ?? 0), 0) ?? 0;
  const thisWeek = frequency.data?.points.at(-1)?.workouts ?? 0;

  return (
    <section className="fade-in">
      <header
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 12,
          padding: '6px 0 14px',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <span
            style={{
              ...EYEBROW,
              display: 'block',
              marginBottom: 6,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {data.user.email}
          </span>
          <h1
            style={{
              margin: 0,
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-title-size)',
              lineHeight: 'var(--text-title-line)',
              letterSpacing: 'var(--text-title-tracking)',
              fontWeight: 700,
              color: 'var(--ink-1)',
            }}
          >
            {data.user.name ?? 'OnFlow User'}
          </h1>
        </div>
        <span
          aria-hidden
          style={{
            width: 46,
            height: 46,
            flexShrink: 0,
            borderRadius: 'var(--r-full)',
            background: 'linear-gradient(145deg, var(--ember-lit), var(--ember-deep))',
            display: 'grid',
            placeItems: 'center',
            fontFamily: 'var(--font-display)',
            fontSize: 19,
            fontWeight: 700,
            color: 'var(--ember-ink)',
            boxShadow: 'var(--elev-2)',
          }}
        >
          {getInitials(data.user.name ?? data.user.email)}
        </span>
      </header>

      <div style={{ ...CARD, borderRadius: 'var(--r-lg)', padding: 22 }}>
        {aiProfile.isLoading ? (
          <SkeletonBlock width="100%" height={116} />
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
              <TrackRing
                progress={progress}
                elapsed={elapsed}
                state={ringState}
                size={116}
                value={`${Math.round(progress)}%`}
              />
              <div style={{ minWidth: 0 }}>
                <span style={{ ...EYEBROW, fontSize: 11, display: 'block' }}>Locked goal</span>
                <p
                  style={{
                    margin: '8px 0 0',
                    fontFamily: 'var(--font-display)',
                    fontSize: 18,
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    lineHeight: 1.2,
                    color: 'var(--ink-1)',
                  }}
                >
                  {goalHeadline(p)}
                </p>
                <p
                  style={{
                    margin: '6px 0 0',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 11,
                    color: 'var(--ink-3)',
                  }}
                >
                  {p?.targetDate
                    ? `by ${new Date(p.targetDate).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                      })}${left != null ? ` · ${left} days left` : ''}`
                    : 'No target date set'}
                </p>
                <div style={{ marginTop: 10 }}>
                  <FlowStatus state={state} />
                </div>
              </div>
            </div>
            <div style={{ marginTop: 16 }}>
              <FlowButton variant="soft" size="sm" block href="/onboarding/mode">
                Change goal
              </FlowButton>
            </div>
          </>
        )}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${trainingEnabled && nutritionEnabled ? 3 : trainingEnabled ? 2 : 1}, 1fr)`,
          gap: 10,
          marginTop: 14,
        }}
      >
        {trainingEnabled && (
          <>
            <StatTile label="This week" value={String(thisWeek)} suffix="sessions" />
            <StatTile label="Sessions" value={String(totalWorkouts)} suffix="total" />
          </>
        )}
        {nutritionEnabled && (
          <StatTile
            label="Logged"
            value={perDay ? String(perDay.daysLogged) : '-'}
            suffix={perDay ? 'of 7 days' : ''}
          />
        )}
      </div>

      {billing.data?.billingEnabled && (
        <>
          <div style={{ ...EYEBROW, margin: '22px 0 10px' }}>Subscription</div>
          <SubscriptionCard status={billing.data} />
        </>
      )}

      <div style={{ ...EYEBROW, margin: '22px 0 10px' }}>Body</div>
      <WeightLogWidget fullWidth />
      <RowGroup
        rows={[
          {
            label: 'Weight',
            value: latestWeightKg != null ? `${latestWeightKg.toFixed(1)} kg` : null,
            to: '/profile',
          },
          { label: 'Height', value: p?.heightCm != null ? `${p.heightCm} cm` : null },
          { label: 'Age', value: p?.age != null ? String(p.age) : null },
          { label: 'Sex', value: genderLabel(p?.gender ?? null) },
          {
            label: 'Activity',
            value: activityLabel(p?.onboardingData?.computedActivityMultiplier),
          },
        ]}
      />

      <div style={{ ...EYEBROW, margin: '22px 0 10px' }}>Tracking</div>
      <RowGroup
        rows={[
          ...(trainingEnabled
            ? [
                {
                  label: 'Routines',
                  value: routines.isLoading ? '…' : `${routines.data?.length ?? 0} saved`,
                  to: '/routines',
                },
                { label: 'Weekly sessions', value: String(data.profile.weeklyGoal) },
              ]
            : []),
          ...(nutritionEnabled
            ? [
                {
                  label: 'Daily target',
                  value: target.data?.target
                    ? `${target.data.target.calorieMin}–${target.data.target.calorieMax} kcal`
                    : null,
                  to: '/log',
                },
                {
                  label: 'Protein',
                  value: target.data?.target
                    ? `${target.data.target.proteinMinG}–${target.data.target.proteinMaxG} g`
                    : null,
                },
                { label: 'Food log', value: 'Open', to: '/log' },
              ]
            : []),
        ]}
      />

      <div style={{ ...EYEBROW, margin: '22px 0 10px' }}>Preferences</div>
      <div style={{ ...CARD, padding: 18 }}>
        <span style={{ ...EYEBROW, fontSize: 11, display: 'block', marginBottom: 10 }}>Units</span>
        <FlowSegment
          aria-label="Units"
          items={[
            { id: 'KG', label: 'kg' },
            { id: 'LB', label: 'lb' },
          ]}
          active={data.profile.units}
          onSelect={(id) => updateProfile.mutate({ units: id as Units })}
        />

        <span style={{ ...EYEBROW, fontSize: 11, display: 'block', margin: '18px 0 10px' }}>
          Rest timer default
        </span>
        <div style={{ display: 'flex', gap: 6 }}>
          {REST_OPTIONS.map((seconds) => {
            const on = data.profile.defaultRestTimer === seconds;
            return (
              <button
                key={seconds}
                type="button"
                aria-pressed={on}
                onClick={() => updateProfile.mutate({ defaultRestTimer: seconds })}
                style={{
                  flex: 1,
                  padding: '10px 0',
                  borderRadius: 'var(--r-full)',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 12,
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                  border: `1px solid ${
                    on ? 'color-mix(in srgb, var(--ember) 45%, transparent)' : 'var(--edge)'
                  }`,
                  background: on ? 'var(--ember-wash)' : 'var(--paper-3)',
                  color: on ? 'var(--ember)' : 'var(--ink-2)',
                }}
              >
                {seconds}s
              </button>
            );
          })}
        </div>
        {updateProfile.isError && (
          <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--off-track)' }} role="alert">
            Couldn&apos;t save that preference. Try again.
          </p>
        )}
      </div>

      <div style={{ ...EYEBROW, margin: '22px 0 10px' }}>Account</div>
      <RowGroup
        rows={[
          { label: 'Tracker setup', value: 'Edit', to: '/onboarding/mode' },
          { label: 'History', value: 'View', to: '/history' },
        ]}
      />

      <div style={{ margin: '14px 0 32px' }}>
        <FlowButton
          variant="danger"
          block
          onClick={() => void logout().then(() => window.location.assign('/login'))}
        >
          Sign out
        </FlowButton>
      </div>
    </section>
  );
}

function StatTile({ label, value, suffix }: { label: string; value: string; suffix: string }) {
  return (
    <div style={{ ...CARD, padding: 14 }}>
      <span style={{ ...EYEBROW, fontSize: 11, display: 'block' }}>{label}</span>
      <div
        style={{
          marginTop: 8,
          fontFamily: 'var(--font-display)',
          fontSize: 'var(--text-metric-sm-size)',
          fontWeight: 700,
          letterSpacing: '-0.03em',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
        <span style={{ color: 'var(--ink-3)', fontWeight: 600, fontSize: '0.42em', marginLeft: 4 }}>
          {suffix}
        </span>
      </div>
    </div>
  );
}

/**
 * Subscription status card — reads the billing entitlement and surfaces
 * subscribed-vs-not at a glance, with a route into /subscribe to manage,
 * upgrade, or start a plan. Only rendered when billingEnabled is true.
 */
export function SubscriptionCard({ status }: { status: BillingStatusResponse }) {
  const cancel = useCancelBillingSubscription();
  const { state, label, cta } = entitlementView(status);
  const priceInr = Math.round(status.product.amountPaise / 100);
  const renewIso = status.subscription?.currentPeriodEnd ?? null;
  const sub = status.subscription;
  const canCancel =
    !!sub &&
    ['active', 'authenticated', 'pending'].includes(sub.providerStatus) &&
    !sub.cancelAtCycleEnd;
  const detail =
    status.entitlement === 'active'
      ? `${status.subscription?.cancelAtCycleEnd ? 'Ends' : 'Renews'}${
          renewIso ? ` ${formatDay(renewIso)}` : ''
        }`
      : status.entitlement === 'trialing'
        ? `${status.daysRemaining} day${status.daysRemaining === 1 ? '' : 's'} left`
        : status.entitlement === 'grace'
          ? `Update payment to keep access`
          : `Subscribe to keep your plan`;

  return (
    <div style={{ ...CARD, padding: 18 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <FlowStatus state={state} label={label} />
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--ink-1)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          ₹{priceInr}/mo
        </span>
      </div>
      <p
        style={{
          margin: '12px 0 0',
          fontFamily: 'var(--font-mono)',
          fontSize: 12,
          color: 'var(--ink-3)',
        }}
      >
        {detail}
      </p>
      <div style={{ marginTop: 14 }}>
        <FlowButton variant="soft" size="sm" block href="/subscribe">
          {cta}
        </FlowButton>
      </div>
      {canCancel && (
        <div style={{ marginTop: 8 }}>
          <FlowButton
            variant="quiet"
            size="sm"
            block
            disabled={cancel.isPending}
            onClick={() => cancel.mutate({ atCycleEnd: true })}
          >
            {cancel.isPending ? 'Cancelling…' : 'Cancel at cycle end'}
          </FlowButton>
        </div>
      )}
      {cancel.isError && (
        <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--off-track)' }} role="alert">
          Couldn&apos;t schedule cancellation. Try again.
        </p>
      )}
    </div>
  );
}

/** Map the billing entitlement to a pill state, its label, and the CTA verb. */
function entitlementView(status: BillingStatusResponse): {
  state: TrackState;
  label: string;
  cta: string;
} {
  switch (status.entitlement) {
    case 'active':
      return { state: 'on-track', label: 'Active', cta: 'Manage subscription' };
    case 'trialing':
      return { state: 'drifting', label: 'Free trial', cta: 'Subscribe' };
    case 'grace':
      return { state: 'off-track', label: 'Payment due', cta: 'Fix payment' };
    case 'locked':
    default:
      return { state: 'neutral', label: 'Not subscribed', cta: 'Subscribe' };
  }
}

function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'Asia/Kolkata',
  });
}

interface RowSpec {
  label: string;
  value: string | null;
  to?: string;
}

/** A card of hairline-separated rows. Rows with a `to` are links. */
function RowGroup({ rows }: { rows: ReadonlyArray<RowSpec> }) {
  return (
    <div style={{ ...CARD, overflow: 'hidden' }}>
      {rows.map((r, i) => {
        const body = (
          <>
            <span style={{ fontSize: 14, color: 'var(--ink-2)' }}>{r.label}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 13,
                  color: r.value ? 'var(--ink-1)' : 'var(--ink-3)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {r.value ?? '-'}
              </span>
              {r.to && (
                <span style={{ color: 'var(--ink-3)', display: 'grid', placeItems: 'center' }}>
                  <Icon name="arrowRight" size={15} />
                </span>
              )}
            </span>
          </>
        );
        const style = {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          width: '100%',
          padding: '13px 15px',
          background: 'transparent',
          border: 0,
          borderTop: i === 0 ? undefined : '1px solid var(--edge)',
          textAlign: 'left' as const,
          textDecoration: 'none',
        };
        return r.to ? (
          <Link key={r.label} to={r.to} style={style}>
            {body}
          </Link>
        ) : (
          <div key={r.label} style={style}>
            {body}
          </div>
        );
      })}
    </div>
  );
}

/** Share of the goal window that has already gone by, 0-100. */
function windowElapsedPct(startIso: string | null, targetIso: string | null, now: Date): number {
  if (!startIso || !targetIso) return 0;
  const start = new Date(startIso).getTime();
  const end = new Date(targetIso).getTime();
  if (end <= start) return 100;
  return Math.max(0, Math.min(100, ((now.getTime() - start) / (end - start)) * 100));
}

function genderLabel(g: Gender | null): string | null {
  if (g === 'male') return 'Male';
  if (g === 'female') return 'Female';
  if (g === 'prefer_not_to_say') return 'Prefer not to say';
  return null;
}

function activityLabel(multiplier: number | undefined): string | null {
  if (multiplier == null) return null;
  if (multiplier < 1.3) return 'Sedentary';
  if (multiplier < 1.5) return 'Light';
  if (multiplier < 1.7) return 'Moderate';
  if (multiplier < 1.85) return 'Active';
  return 'Very active';
}

function getInitials(nameOrEmail: string): string {
  const parts = nameOrEmail
    .trim()
    .split(/[\s@.]+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}

// `UserProfileAI` is referenced only through the hook's inferred type; the
// import keeps the shape documented at the top of the file.
export type { UserProfileAI };
